import axios, { type AxiosRequestConfig } from 'axios';
import { useContext, useMemo } from 'react';
import { config } from '../../../modules/config';
import { AuthContext } from '../../../modules/context/authContext';
import type { SuccessEnvelop } from '../../../modules/context/guestAuthContext';
import { UserPropertiesContext } from '../../../modules/context/userPropertiesContext';
import type { GuestTheme, GuestThemeRecord } from '../../../modules/interfaces';
import { endpoints } from '../../../modules/mappers/urls';

/**
 * Admin CRUD for guest-portal themes against `v1/guest-portal-themes`.
 *
 * These run from imperative event handlers (table actions, modal save) against
 * per-row `/:id` URLs, which `useMutationRequest` (fixed URL at hook-init) doesn't
 * fit — so this is a thin axios-backed service hook. Requests still flow through the
 * global axios interceptors (transaction ids + centralized error logging). The
 * Cognito token is passed explicitly (it is not auto-injected) and the selected
 * property is sent as the `property-id` header, matching the rest of the app.
 */
export interface GuestPortalThemesService {
  list: (propertyId: string) => Promise<GuestThemeRecord[]>;
  create: (
    propertyId: string,
    name: string,
    theme: GuestTheme,
  ) => Promise<GuestThemeRecord>;
  update: (
    id: string,
    changes: { name?: string; theme?: GuestTheme },
  ) => Promise<GuestThemeRecord>;
  remove: (id: string) => Promise<void>;
  activate: (id: string) => Promise<void>;
  deactivate: (id: string) => Promise<void>;
}

export const useGuestPortalThemes = (): GuestPortalThemesService => {
  const { credentialsInfo } = useContext(AuthContext) || {};
  const { selectedProperty } = useContext(UserPropertiesContext)!;

  const token = credentialsInfo?.token;
  const propertyHeaderId = selectedProperty?.property.uuid;

  return useMemo<GuestPortalThemesService>(() => {
    const request = async <R>(reqConfig: AxiosRequestConfig): Promise<R> => {
      const headers: Record<string, string> = {};
      if (token) headers.authorization = token;
      if (propertyHeaderId) headers['property-id'] = propertyHeaderId;

      const { data } = await axios.request<SuccessEnvelop<R>>({
        baseURL: config.bookingEngine.endpoint,
        ...reqConfig,
        headers: { ...headers, ...reqConfig.headers },
      });
      return data.result as R;
    };

    return {
      list: (propertyId) =>
        request<GuestThemeRecord[]>({
          url: endpoints.GUEST_PORTAL_THEMES,
          method: 'get',
          params: { propertyId },
        }),

      create: (propertyId, name, theme) =>
        request<GuestThemeRecord>({
          url: endpoints.GUEST_PORTAL_THEMES,
          method: 'post',
          data: { propertyId, name, theme },
        }),

      update: (id, changes) =>
        request<GuestThemeRecord>({
          url: `${endpoints.GUEST_PORTAL_THEMES}/${id}`,
          method: 'put',
          data: changes,
        }),

      remove: (id) =>
        request<void>({
          url: `${endpoints.GUEST_PORTAL_THEMES}/${id}`,
          method: 'delete',
        }),

      activate: (id) =>
        request<void>({
          url: `${endpoints.GUEST_PORTAL_THEMES}/${id}/activate`,
          method: 'post',
        }),

      deactivate: (id) =>
        request<void>({
          url: `${endpoints.GUEST_PORTAL_THEMES}/${id}/deactivate`,
          method: 'post',
        }),
    };
  }, [token, propertyHeaderId]);
};
